import type { PublishedCourse } from '@slideforge/shared';

/**
 * Package manifests.
 *
 * Written by hand against the published specifications rather than delegated to
 * a library: conformance is the product's selling point (docs/plans/41 §7), and
 * a manifest is the one artefact an LMS reads before it will accept anything
 * else. Element order inside `<manifest>` is fixed by the XSD — metadata,
 * organizations, resources — and an LMS that validates will reject a file that
 * merely has the right elements in the wrong sequence.
 */

export type ScormSequencing = 'SingleSCO' | 'ChaptersFree' | 'ChaptersSequential';

export interface ManifestOptions {
  readonly course: PublishedCourse;
  /** Every file in the package, package-relative, forward slashes. */
  readonly files: readonly string[];
  readonly entryPoint: string;
  readonly sequencing?: ScormSequencing;
  /** 0-1. Absent when the course has no graded quiz. */
  readonly masteryScore?: number;
}

export function scorm12Manifest(options: ManifestOptions): string {
  const { course, files, entryPoint } = options;
  const id = identifier(course.courseId);
  const mastery =
    options.masteryScore === undefined
      ? ''
      : `\n        <adlcp:masteryscore>${Math.round(options.masteryScore * 100)}</adlcp:masteryscore>`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<manifest identifier="${id}" version="1.2"
  xmlns="http://www.imsproject.org/xsd/imscp_rootv1p1p2"
  xmlns:adlcp="http://www.adlnet.org/xsd/adlcp_rootv1p2"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsproject.org/xsd/imscp_rootv1p1p2 imscp_rootv1p1p2.xsd http://www.imsglobal.org/xsd/imsmd_rootv1p2p1 imsmd_rootv1p2p1.xsd http://www.adlnet.org/xsd/adlcp_rootv1p2 adlcp_rootv1p2.xsd">
  <metadata>
    <schema>ADL SCORM</schema>
    <schemaversion>1.2</schemaversion>
  </metadata>
  <organizations default="${id}-ORG">
    <organization identifier="${id}-ORG">
      <title>${escapeXml(course.title)}</title>
      <item identifier="${id}-ITEM" identifierref="${id}-RES" isvisible="true">
        <title>${escapeXml(course.title)}</title>${mastery}
      </item>
    </organization>
  </organizations>
  <resources>
    <resource identifier="${id}-RES" type="webcontent" adlcp:scormtype="sco" href="${escapeXml(entryPoint)}">
${fileElements(files)}
    </resource>
  </resources>
</manifest>
`;
}

export function scorm2004Manifest(options: ManifestOptions): string {
  const { course, files, entryPoint } = options;
  const id = identifier(course.courseId);
  const sequencing = options.sequencing ?? 'SingleSCO';

  // `choice` lets the learner jump between items; `flow` drives Next/Previous.
  // Forced-sequential is exactly choice="false", and it is the preset a lecturer
  // reaches for when a course must be worked through in order.
  const controlMode =
    sequencing === 'ChaptersSequential'
      ? '<imsss:controlMode choice="false" flow="true" forwardOnly="true"/>'
      : '<imsss:controlMode choice="true" flow="true"/>';

  const objective =
    options.masteryScore === undefined
      ? `        <imsss:objectives>
          <imsss:primaryObjective objectiveID="${id}-OBJ"/>
        </imsss:objectives>
`
      : `        <imsss:objectives>
          <imsss:primaryObjective objectiveID="${id}-OBJ" satisfiedByMeasure="true">
            <imsss:minNormalizedMeasure>${options.masteryScore.toFixed(4)}</imsss:minNormalizedMeasure>
          </imsss:primaryObjective>
        </imsss:objectives>
`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<manifest identifier="${id}" version="1"
  xmlns="http://www.imsglobal.org/xsd/imscp_v1p1"
  xmlns:adlcp="http://www.adlnet.org/xsd/adlcp_v1p3"
  xmlns:adlseq="http://www.adlnet.org/xsd/adlseq_v1p3"
  xmlns:adlnav="http://www.adlnet.org/xsd/adlnav_v1p3"
  xmlns:imsss="http://www.imsglobal.org/xsd/imsss"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsglobal.org/xsd/imscp_v1p1 imscp_v1p1.xsd http://www.adlnet.org/xsd/adlcp_v1p3 adlcp_v1p3.xsd http://www.adlnet.org/xsd/adlseq_v1p3 adlseq_v1p3.xsd http://www.adlnet.org/xsd/adlnav_v1p3 adlnav_v1p3.xsd http://www.imsglobal.org/xsd/imsss imsss_v1p0.xsd">
  <metadata>
    <schema>ADL SCORM</schema>
    <schemaversion>2004 4th Edition</schemaversion>
  </metadata>
  <organizations default="${id}-ORG">
    <organization identifier="${id}-ORG">
      <title>${escapeXml(course.title)}</title>
      <item identifier="${id}-ITEM" identifierref="${id}-RES">
        <title>${escapeXml(course.title)}</title>
        <imsss:sequencing>
${objective}          <imsss:deliveryControls completionSetByContent="true" objectiveSetByContent="true"/>
        </imsss:sequencing>
      </item>
      <imsss:sequencing>
        ${controlMode}
        <imsss:rollupRules rollupObjectiveSatisfied="true" rollupProgressCompletion="true"/>
      </imsss:sequencing>
    </organization>
  </organizations>
  <resources>
    <resource identifier="${id}-RES" type="webcontent" adlcp:scormType="sco" href="${escapeXml(entryPoint)}">
${fileElements(files)}
    </resource>
  </resources>
</manifest>
`;
}

export function tincanXml(options: ManifestOptions): string {
  const { course, entryPoint } = options;
  const lang = course.language;
  return `<?xml version="1.0" encoding="UTF-8"?>
<tincan xmlns="http://projecttincan.com/tincan.xsd">
  <activities>
    <activity id="${escapeXml(activityId(course))}" type="http://adlnet.gov/expapi/activities/course">
      <name lang="${escapeXml(lang)}">${escapeXml(course.title)}</name>
      <description lang="${escapeXml(lang)}">${escapeXml(course.description)}</description>
      <launch lang="${escapeXml(lang)}">${escapeXml(entryPoint)}</launch>
    </activity>
${course.slides
  .filter((s) => s.quiz)
  .map(
    (s) => `    <activity id="${escapeXml(activityId(course))}/question/${escapeXml(s.id)}" type="http://adlnet.gov/expapi/activities/assessment">
      <name lang="${escapeXml(lang)}">${escapeXml(s.quiz?.title ?? s.title)}</name>
    </activity>`,
  )
  .join('\n')}
  </activities>
</tincan>
`;
}

export interface Cmi5Options extends ManifestOptions {
  readonly moveOn?: 'Passed' | 'Completed' | 'CompletedAndPassed' | 'CompletedOrPassed' | 'NotApplicable';
  readonly launchMethod?: 'OwnWindow' | 'AnyWindow';
}

export function cmi5Xml(options: Cmi5Options): string {
  const { course, entryPoint } = options;
  const lang = course.language;
  const id = activityId(course);
  const mastery =
    options.masteryScore === undefined ? '' : ` masteryScore="${options.masteryScore.toFixed(4)}"`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<courseStructure xmlns="https://w3id.org/xapi/profiles/cmi5/v1/CourseStructure.xsd" id="${escapeXml(id)}">
  <course id="${escapeXml(id)}">
    <title><langstring lang="${escapeXml(lang)}">${escapeXml(course.title)}</langstring></title>
    <description><langstring lang="${escapeXml(lang)}">${escapeXml(course.description)}</langstring></description>
  </course>
  <au id="${escapeXml(id)}/au/1" moveOn="${options.moveOn ?? 'CompletedOrPassed'}"${mastery} launchMethod="${options.launchMethod ?? 'AnyWindow'}">
    <title><langstring lang="${escapeXml(lang)}">${escapeXml(course.title)}</langstring></title>
    <description><langstring lang="${escapeXml(lang)}">${escapeXml(course.description)}</langstring></description>
    <url>${escapeXml(entryPoint)}</url>
  </au>
</courseStructure>
`;
}

function fileElements(files: readonly string[]): string {
  return [...files]
    .sort()
    .map((f) => `      <file href="${escapeXml(f)}"/>`)
    .join('\n');
}

export function activityId(course: PublishedCourse): string {
  // A bare id is not a valid xAPI activity id; the IRI form is required.
  return /^[a-z][a-z0-9+.-]*:/i.test(course.courseId)
    ? course.courseId
    : `https://slideforge.local/course/${encodeURIComponent(course.courseId)}`;
}

/** Manifest identifiers must be XML NMTOKENs, which most course ids are not. */
export function identifier(raw: string): string {
  const cleaned = raw.replace(/[^A-Za-z0-9_.-]/g, '-');
  return /^[A-Za-z_]/.test(cleaned) ? cleaned : `SF-${cleaned}`;
}

export function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
